/**
 * TMR Socket.IO Manager — production reconnection for live market feeds.
 *
 * Strategy:
 *  - Single shared connection per browser tab (no duplicate sockets).
 *  - Socket.IO reconnection with exponential backoff: 1s → 2s → 4s … max 30s (+ jitter).
 *  - Client heartbeat (ping ack) + server heartbeat watchdog to kill zombie connections.
 *  - Global symbol room registry — re-subscribe ALL rooms after every reconnect.
 *  - Page Visibility: when tab becomes visible again, force reconnect if stale/down.
 *  - Circuit breaker: after many consecutive failures, cool down then resume.
 *  - HTTP polling fallback remains in useLiveBoardOhlc (this module only owns the WS).
 *
 * Usage (imperative):
 *   import { tmrSocket } from '../services/tmrSocketManager';
 *   const unsub = tmrSocket.acquire({ symbols: ['NIFTY'], onOhlc, onStatus });
 *   // later: unsub()
 */

import { io } from 'socket.io-client';

const LOG = '[TMR-WS]';

/** @typedef {'idle'|'connecting'|'connected'|'reconnecting'|'disconnected'|'offline'} SocketPhase */

const DEFAULTS = {
  path: '/ws/socket.io',
  /** Base delay for first reconnect attempt (ms) */
  reconnectDelay: 1000,
  /** Cap for exponential backoff (ms) */
  reconnectDelayMax: 30000,
  /** Socket.IO connect timeout */
  timeout: 12000,
  /** How often we emit client ping and expect ack */
  clientPingMs: 15000,
  /** If no server activity (heartbeat/message/pong) for this long → force reconnect */
  zombieTimeoutMs: 45000,
  /** After this many consecutive failed reconnects, open circuit for cooldown */
  circuitFailThreshold: 12,
  /** Cooldown when circuit opens (ms) */
  circuitCooldownMs: 60000,
  transports: ['websocket', 'polling'],
};

function log(...args) {
  try {
    // eslint-disable-next-line no-console
    console.info(LOG, ...args);
  } catch (_) { /* noop */ }
}

function warn(...args) {
  try {
    // eslint-disable-next-line no-console
    console.warn(LOG, ...args);
  } catch (_) { /* noop */ }
}

function now() {
  return Date.now();
}

/**
 * Exponential backoff with full jitter (AWS-style).
 * attempt is 1-based.
 */
export function backoffMs(attempt, base = DEFAULTS.reconnectDelay, max = DEFAULTS.reconnectDelayMax) {
  const exp = Math.min(max, base * (2 ** Math.max(0, attempt - 1)));
  // Full jitter: random in [0, exp], but never below base/2 for first few tries
  const floor = attempt <= 1 ? base : Math.min(base, exp * 0.25);
  return Math.floor(floor + Math.random() * (exp - floor));
}

class TmrSocketManager {
  constructor(opts = {}) {
    this.opts = { ...DEFAULTS, ...opts };
    /** @type {import('socket.io-client').Socket | null} */
    this.socket = null;
    this.wsUrl = '';
    this.refCount = 0;

    /** @type {Map<string, number>} symbol → subscriber count */
    this.symbolRefs = new Map();

    /** @type {Set<(patch: object) => void>} */
    this.statusListeners = new Set();
    /** @type {Set<(u: object) => void>} */
    this.ohlcListeners = new Set();
    /** @type {Set<(u: object) => void>} */
    this.ocListeners = new Set();
    /** @type {Set<(u: object) => void>} */
    this.tickListeners = new Set();

    /** @type {SocketPhase} */
    this.phase = 'idle';
    this.lastError = null;
    this.lastConnectedAt = 0;
    this.lastDisconnectAt = 0;
    this.lastActivityAt = 0;
    this.lastHeartbeatAt = 0;
    this.reconnectAttempt = 0;
    this.consecutiveFailures = 0;
    this.circuitOpenUntil = 0;

    this._clientPingTimer = null;
    this._zombieTimer = null;
    this._manualReconnectTimer = null;
    this._visibilityBound = false;
    this._handlersBound = false;
  }

  // ─── Public API ──────────────────────────────────────────────────────────

  /**
   * Acquire a share of the connection. Returns a disposer that releases it.
   * @param {object} opts
   * @param {string[]} [opts.symbols]
   * @param {string} [opts.wsUrl]
   * @param {(patch: object) => void} [opts.onStatus]
   * @param {(u: object) => void} [opts.onOhlc]
   * @param {(u: object) => void} [opts.onOc]
   * @param {(u: object) => void} [opts.onTick]
   */
  acquire({
    symbols = [],
    wsUrl = '',
    onStatus,
    onOhlc,
    onOc,
    onTick,
  } = {}) {
    this.refCount += 1;
    if (wsUrl) this.wsUrl = wsUrl;

    if (onStatus) this.statusListeners.add(onStatus);
    if (onOhlc) this.ohlcListeners.add(onOhlc);
    if (onOc) this.ocListeners.add(onOc);
    if (onTick) this.tickListeners.add(onTick);

    const symList = (symbols || []).map((s) => String(s).toUpperCase()).filter(Boolean);
    symList.forEach((s) => this._addSymbol(s));

    this._ensureSocket();
    this._bindVisibility();

    // Immediate status snapshot for this subscriber
    if (onStatus) {
      try { onStatus(this.getStatus()); } catch (_) { /* noop */ }
    }

    // If already connected, subscribe rooms now
    if (this.socket?.connected) {
      this._subscribeAll('acquire');
    }

    let released = false;
    return () => {
      if (released) return;
      released = true;
      symList.forEach((s) => this._removeSymbol(s));
      if (onStatus) this.statusListeners.delete(onStatus);
      if (onOhlc) this.ohlcListeners.delete(onOhlc);
      if (onOc) this.ocListeners.delete(onOc);
      if (onTick) this.tickListeners.delete(onTick);
      this.refCount = Math.max(0, this.refCount - 1);
      if (this.refCount === 0) {
        this._teardown('no-refs');
      }
    };
  }

  getStatus() {
    return {
      socket: this.phase,
      mode: this._modeFromPhase(this.phase),
      connected: this.phase === 'connected' && !!this.socket?.connected,
      phase: this.phase,
      error: this.lastError,
      lastConnectedAt: this.lastConnectedAt,
      lastDisconnectAt: this.lastDisconnectAt,
      lastActivityAt: this.lastActivityAt,
      lastHeartbeatAt: this.lastHeartbeatAt,
      reconnectAttempt: this.reconnectAttempt,
      consecutiveFailures: this.consecutiveFailures,
      circuitOpen: this.circuitOpenUntil > now(),
      circuitOpenUntil: this.circuitOpenUntil,
      symbols: [...this.symbolRefs.keys()],
      at: now(),
    };
  }

  /** Force a clean reconnect (e.g. after visibility resume or zombie). */
  forceReconnect(reason = 'manual') {
    log('forceReconnect', reason);
    if (this.refCount <= 0) return;
    if (this.circuitOpenUntil > now()) {
      const wait = this.circuitOpenUntil - now();
      log('circuit open, defer forceReconnect', wait, 'ms');
      this._scheduleManualReconnect(wait, 'circuit-defer');
      return;
    }
    this._setPhase('reconnecting', { reason });
    this._destroySocket('force:' + reason);
    this._ensureSocket();
  }

  getSocket() {
    return this.socket;
  }

  // ─── Internals ───────────────────────────────────────────────────────────

  _modeFromPhase(phase) {
    switch (phase) {
      case 'connected': return 'live';
      case 'connecting':
      case 'reconnecting': return 'reconnecting';
      case 'offline': return 'offline';
      case 'disconnected': return 'polling';
      default: return 'idle';
    }
  }

  _setPhase(phase, extra = {}) {
    this.phase = phase;
    if (extra.error !== undefined) this.lastError = extra.error;
    const patch = { ...this.getStatus(), ...extra, socket: phase, mode: this._modeFromPhase(phase) };
    this.statusListeners.forEach((fn) => {
      try { fn(patch); } catch (e) { warn('status listener error', e); }
    });
  }

  _touchActivity(kind = 'activity') {
    this.lastActivityAt = now();
    if (kind === 'heartbeat') this.lastHeartbeatAt = this.lastActivityAt;
  }

  _addSymbol(sym) {
    const n = (this.symbolRefs.get(sym) || 0) + 1;
    this.symbolRefs.set(sym, n);
    if (n === 1 && this.socket?.connected) {
      this.socket.emit('subscribe', { symbol: sym });
      log('subscribe', sym);
    }
  }

  _removeSymbol(sym) {
    const n = (this.symbolRefs.get(sym) || 0) - 1;
    if (n <= 0) {
      this.symbolRefs.delete(sym);
      if (this.socket?.connected) {
        try { this.socket.emit('unsubscribe', { symbol: sym }); } catch (_) { /* noop */ }
        log('unsubscribe', sym);
      }
    } else {
      this.symbolRefs.set(sym, n);
    }
  }

  _subscribeAll(reason = '') {
    if (!this.socket?.connected) return;
    const symbols = [...this.symbolRefs.keys()];
    if (!symbols.length) return;
    this.socket.emit('subscribe', { symbols });
    log('re-subscribe', symbols.join(','), reason ? `(${reason})` : '');
  }

  _ensureSocket() {
    if (this.socket) return this.socket;
    if (this.refCount <= 0) return null;

    if (this.circuitOpenUntil > now()) {
      const wait = this.circuitOpenUntil - now();
      this._setPhase('offline', { error: 'circuit open — cooling down' });
      this._scheduleManualReconnect(wait, 'circuit');
      return null;
    }

    const url = this.wsUrl || (typeof window !== 'undefined' ? window.location.origin : '');
    log('creating socket', url, 'attempt', this.reconnectAttempt);

    this._setPhase(this.reconnectAttempt > 0 ? 'reconnecting' : 'connecting');

    const socket = io(url, {
      path: this.opts.path,
      transports: this.opts.transports,
      withCredentials: true,
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: this.opts.reconnectDelay,
      reconnectionDelayMax: this.opts.reconnectDelayMax,
      // randomizationFactor works with Socket.IO Manager for jitter
      randomizationFactor: 0.5,
      timeout: this.opts.timeout,
      autoConnect: true,
      // Prefer websocket; fall back to polling if upgrade fails
      upgrade: true,
      rememberUpgrade: true,
    });

    this.socket = socket;
    this._bindSocketHandlers(socket);
    return socket;
  }

  _bindSocketHandlers(socket) {
    if (this._handlersBound) return;
    this._handlersBound = true;

    socket.on('connect', () => {
      const id = socket.id;
      log('connected', id, 'recovered after attempts=', this.reconnectAttempt);
      this.reconnectAttempt = 0;
      this.consecutiveFailures = 0;
      this.circuitOpenUntil = 0;
      this.lastConnectedAt = now();
      this.lastError = null;
      this._touchActivity('connect');
      this._setPhase('connected', { error: null, socketId: id });
      this._subscribeAll('connect');
      this._startWatchdogs();
    });

    socket.on('disconnect', (reason) => {
      log('disconnect', reason);
      this.lastDisconnectAt = now();
      this._stopWatchdogs();
      // "io client disconnect" = intentional teardown — don't mark reconnecting
      if (reason === 'io client disconnect') {
        this._setPhase('disconnected', { reason });
        return;
      }
      this.reconnectAttempt += 1;
      this._setPhase('reconnecting', { reason, reconnectAttempt: this.reconnectAttempt });
    });

    socket.on('connect_error', (err) => {
      this.consecutiveFailures += 1;
      this.lastError = err?.message || 'connect_error';
      warn('connect_error', this.lastError, 'failures=', this.consecutiveFailures);
      this._maybeOpenCircuit();
      this._setPhase('reconnecting', {
        error: this.lastError,
        reconnectAttempt: this.reconnectAttempt + 1,
      });
    });

    const mgr = socket.io;
    if (mgr) {
      mgr.on('reconnect_attempt', (n) => {
        this.reconnectAttempt = n;
        const delay = backoffMs(n, this.opts.reconnectDelay, this.opts.reconnectDelayMax);
        log('reconnect_attempt', n, '≈delay', delay);
        this._setPhase('reconnecting', { reconnectAttempt: n });
      });

      mgr.on('reconnect', (n) => {
        log('reconnect success after', n, 'attempts');
        // connect handler will also fire; keep phase clean
        this.reconnectAttempt = 0;
        this.consecutiveFailures = 0;
      });

      mgr.on('reconnect_error', (err) => {
        this.consecutiveFailures += 1;
        this.lastError = err?.message || 'reconnect_error';
        warn('reconnect_error', this.lastError);
        this._maybeOpenCircuit();
        this._setPhase('reconnecting', { error: this.lastError });
      });

      mgr.on('reconnect_failed', () => {
        // Should be rare with Infinity attempts; treat as circuit trip
        warn('reconnect_failed — opening circuit');
        this.consecutiveFailures = Math.max(this.consecutiveFailures, this.opts.circuitFailThreshold);
        this._maybeOpenCircuit();
        this._setPhase('offline', { error: 'reconnect failed' });
        this._scheduleManualReconnect(this.opts.circuitCooldownMs, 'reconnect_failed');
      });
    }

    socket.on('server:hello', (msg) => {
      this._touchActivity('hello');
      log('server:hello', msg?.redis != null ? `redis=${msg.redis}` : '');
    });

    socket.on('server:heartbeat', (hb) => {
      this._touchActivity('heartbeat');
      this._setPhase('connected', { heartbeat: hb, error: null });
    });

    socket.on('subscribed', (info) => {
      this._touchActivity('subscribed');
      log('subscribed ack', info?.symbol || info);
    });

    socket.on('live:ohlc', (u) => {
      this._touchActivity('ohlc');
      this.ohlcListeners.forEach((fn) => {
        try { fn(u); } catch (e) { warn('ohlc listener', e); }
      });
    });

    socket.on('live:oc', (u) => {
      this._touchActivity('oc');
      this.ocListeners.forEach((fn) => {
        try { fn(u); } catch (e) { warn('oc listener', e); }
      });
    });

    socket.on('live:tick', (u) => {
      this._touchActivity('tick');
      this.tickListeners.forEach((fn) => {
        try { fn(u); } catch (e) { warn('tick listener', e); }
      });
    });

    socket.on('live:update', (u) => {
      this._touchActivity('update');
      if (u?.type === 'live:ohlc') {
        this.ohlcListeners.forEach((fn) => {
          try { fn(u); } catch (_) { /* noop */ }
        });
      } else if (u?.type === 'live:oc') {
        this.ocListeners.forEach((fn) => {
          try { fn(u); } catch (_) { /* noop */ }
        });
      }
    });
  }

  _maybeOpenCircuit() {
    if (this.consecutiveFailures < this.opts.circuitFailThreshold) return;
    this.circuitOpenUntil = now() + this.opts.circuitCooldownMs;
    warn('circuit OPEN for', this.opts.circuitCooldownMs, 'ms after', this.consecutiveFailures, 'failures');
    this._setPhase('offline', { error: `circuit open (${this.consecutiveFailures} failures)` });
    // Pause Socket.IO reconnects during cooldown by disconnecting
    try {
      if (this.socket) {
        this.socket.io.opts.reconnection = false;
        this.socket.disconnect();
      }
    } catch (_) { /* noop */ }
    this._scheduleManualReconnect(this.opts.circuitCooldownMs, 'circuit-open');
  }

  _scheduleManualReconnect(delayMs, reason) {
    if (this._manualReconnectTimer) {
      clearTimeout(this._manualReconnectTimer);
      this._manualReconnectTimer = null;
    }
    if (this.refCount <= 0) return;
    log('schedule reconnect in', delayMs, 'ms —', reason);
    this._manualReconnectTimer = setTimeout(() => {
      this._manualReconnectTimer = null;
      if (this.refCount <= 0) return;
      this.circuitOpenUntil = 0;
      this.consecutiveFailures = 0;
      this._destroySocket('manual-retry:' + reason);
      this._ensureSocket();
    }, Math.max(250, delayMs));
  }

  _startWatchdogs() {
    this._stopWatchdogs();

    // Client → server ping with ack (detect half-open links)
    this._clientPingTimer = setInterval(() => {
      if (!this.socket?.connected) return;
      const t0 = now();
      try {
        this.socket.timeout(8000).emit('ping', (err, ack) => {
          if (err) {
            warn('client ping timeout/error', err?.message || err);
            this.forceReconnect('ping-timeout');
            return;
          }
          this._touchActivity('pong');
          const rtt = now() - t0;
          if (rtt > 5000) log('ping rtt high', rtt, 'ms', ack);
        });
      } catch (e) {
        warn('ping emit failed', e?.message || e);
      }
    }, this.opts.clientPingMs);
    if (this._clientPingTimer.unref) this._clientPingTimer.unref();

    // Zombie detector: no activity for zombieTimeoutMs → force reconnect
    this._zombieTimer = setInterval(() => {
      if (!this.socket?.connected) return;
      const silent = now() - (this.lastActivityAt || this.lastConnectedAt || 0);
      if (silent > this.opts.zombieTimeoutMs) {
        warn('zombie connection detected, silent for', silent, 'ms');
        this.forceReconnect('zombie');
      }
    }, Math.min(10000, Math.floor(this.opts.zombieTimeoutMs / 3)));
    if (this._zombieTimer.unref) this._zombieTimer.unref();
  }

  _stopWatchdogs() {
    if (this._clientPingTimer) {
      clearInterval(this._clientPingTimer);
      this._clientPingTimer = null;
    }
    if (this._zombieTimer) {
      clearInterval(this._zombieTimer);
      this._zombieTimer = null;
    }
  }

  _bindVisibility() {
    if (this._visibilityBound || typeof document === 'undefined') return;
    this._visibilityBound = true;
    this._onVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      if (this.refCount <= 0) return;
      log('tab visible — check connection');
      const connected = !!this.socket?.connected;
      const silent = now() - (this.lastActivityAt || 0);
      if (!connected) {
        this.forceReconnect('visibility-offline');
      } else if (silent > this.opts.zombieTimeoutMs * 0.6) {
        // Stale while backgrounded — soft ping or reconnect
        try {
          this.socket.timeout(5000).emit('ping', (err) => {
            if (err) this.forceReconnect('visibility-stale');
            else this._touchActivity('pong');
          });
        } catch (_) {
          this.forceReconnect('visibility-error');
        }
      } else {
        // Ensure rooms still subscribed after sleep
        this._subscribeAll('visibility');
      }
    };
    document.addEventListener('visibilitychange', this._onVisibility);
    // Online/offline browser events
    this._onOnline = () => {
      log('browser online');
      if (this.refCount > 0 && !this.socket?.connected) {
        this.forceReconnect('browser-online');
      }
    };
    this._onOffline = () => {
      log('browser offline');
      this._setPhase('disconnected', { error: 'browser offline' });
    };
    window.addEventListener('online', this._onOnline);
    window.addEventListener('offline', this._onOffline);
  }

  _unbindVisibility() {
    if (!this._visibilityBound) return;
    this._visibilityBound = false;
    if (this._onVisibility) document.removeEventListener('visibilitychange', this._onVisibility);
    if (this._onOnline) window.removeEventListener('online', this._onOnline);
    if (this._onOffline) window.removeEventListener('offline', this._onOffline);
    this._onVisibility = null;
    this._onOnline = null;
    this._onOffline = null;
  }

  _destroySocket(reason = '') {
    this._stopWatchdogs();
    const s = this.socket;
    this.socket = null;
    this._handlersBound = false;
    if (!s) return;
    log('destroy socket', reason);
    try {
      s.removeAllListeners();
      if (s.io) {
        try { s.io.removeAllListeners(); } catch (_) { /* noop */ }
      }
      s.disconnect();
    } catch (e) {
      warn('destroy error', e?.message || e);
    }
  }

  _teardown(reason = '') {
    log('teardown', reason);
    if (this._manualReconnectTimer) {
      clearTimeout(this._manualReconnectTimer);
      this._manualReconnectTimer = null;
    }
    this._unbindVisibility();
    this._destroySocket(reason);
    this.symbolRefs.clear();
    this.phase = 'idle';
    this.reconnectAttempt = 0;
  }
}

/** Singleton manager for the SPA tab. */
export const tmrSocket = new TmrSocketManager();

export default tmrSocket;
